import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { EntityContentService } from '@proxy/common';
import { UserProfileDto } from '@proxy/users';
import { AppState } from 'src/app/app.state';
import { Store } from '@ngxs/store';
import { GetPageLayout } from 'src/app/app.actions';
import { pluck, take } from 'rxjs/operators';
import { PageLayout, PageLayoutContent, TableView } from 'src/app/app.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ChangeDetectorRef } from '@angular/core';
import { ConfigStateService } from '@abp/ng.core';

@Component({
    selector: 'app-appearance-form',
    templateUrl: './appearance.component.html',
    exportAs: 'abpChangePasswordForm',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppearanceComponent implements OnInit, OnDestroy {
  pageLayoutContent: PageLayoutContent;
  userProfile: UserProfileDto;
  form: FormGroup;
  pageLayout: PageLayout;

  private destroy$ = new Subject<void>();
  private formDestroy$ = new Subject<void>();
  private isUpdating = false; // Prevent circular updates

  constructor(
    private fb: FormBuilder,
    private entityContentService: EntityContentService,
    private store: Store,
    private configService: ConfigStateService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    // Get user profile - use take(1) to only get it once
    this.store.select(AppState.getUserProfile)
      .pipe(take(1))
      .subscribe(u => {
        if (u == null) return;
        this.userProfile = u;

        this.store.select(AppState.getPageLayoutContent)
          .pipe(takeUntil(this.destroy$))
          .subscribe(s => {
            if (this.isUpdating) return;
            const pageLayoutContent = s as PageLayoutContent;
            this.pageLayoutContent = pageLayoutContent;
            this.pageLayout = pageLayoutContent?.pageLayout;
            this.buildForm();
          });
      });
  }

  ngOnDestroy(): void {
    this.formDestroy$.next();
    this.formDestroy$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  buildForm() {
    this.formDestroy$.next();
    // Create form with initial values
    const tableView = this.pageLayout?.tableView ?? TableView.Classic;
    const tableViewStr = String(tableView);
    console.log('buildForm called, pageLayout:', this.pageLayout, 'isUpdating:', this.isUpdating);
    console.log('this.pageLayout', this.pageLayout);

    this.form = this.fb.group({
      tableView: [tableViewStr],
      noAnimations: [this.pageLayout?.noAnimations ?? false],
      showRowNo: [this.pageLayout?.showRowNo ?? false],
      recordsPerPage: [this.pageLayout?.recordsPerPage ?? 50],
    });
    this.cdr.detectChanges();
    // Subscribe to value changes
    setTimeout(() => {
      this.form.valueChanges
        .pipe(takeUntil(this.formDestroy$))
        .subscribe(async formValues => {
          if (this.isUpdating) {
            return;
          }
          this.isUpdating = true;

          try {
            if (this.pageLayoutContent == null) {
              const pageLayoutContent = {
                type: `page-layout/user`,
                content: JSON.stringify(formValues),
                pageLayout: formValues,
                entityId: this.userProfile.userId
              };
              const created = await this.entityContentService.create(pageLayoutContent).toPromise();
              this.pageLayoutContent = created;
            } else {
              this.pageLayoutContent.content = JSON.stringify(formValues);
              this.pageLayoutContent.pageLayout = formValues;
              const updated = await this.entityContentService.update(
                this.pageLayoutContent.id,
                this.pageLayoutContent
              ).toPromise();
              this.pageLayoutContent = updated;
            }

            // Refresh the page layout in store
            this.store
              .dispatch(new GetPageLayout())
              .pipe(
                pluck('AppState', 'pageLayoutContent'),
                take(1)
              )
              .subscribe(s => {
                this.pageLayoutContent = s as PageLayoutContent;
              });
          } catch (error) {
            console.error('Error saving page layout:', error);
          } finally {
            this.isUpdating = false;
          }
        });
    }, 0);
  }

  async resetToDefaults() {
    this.isUpdating = true;

    try {
      const currentUser = this.configService.getOne('currentUser') as any;
      const countResult = await this.entityContentService
        .getList({
          filter: `type eq "page-layout/user"`,
          maxResultCount: 1,
        } as any)
        .toPromise();

      const totalCount = countResult?.totalCount ?? 0;
      const allRecords = await this.entityContentService
        .getList({
          filter: `type eq "page-layout/user"`,
          maxResultCount: totalCount || 200,  // fallback to 200
        } as any)
        .toPromise();

      const userRecords = allRecords?.items?.filter(i => i.entityId === currentUser?.id);
      console.log('Records to delete:', userRecords?.length, 'of total:', totalCount);

      await Promise.all(
        (userRecords ?? []).map(record =>
          this.entityContentService.delete(record.id).toPromise()
        )
      );

      await this.store.dispatch(new GetPageLayout()).toPromise();

      this.isUpdating = false;
      this.pageLayoutContent = null;
      this.pageLayout = null;
      this.buildForm();
      this.cdr.detectChanges();

    } catch (error) {
      console.error('Error:', error);
      this.isUpdating = false;
    }
  }
}